'use client';
import clsx from 'clsx';
import {
    ChangeEvent,
    FC,
    InputHTMLAttributes,
    KeyboardEvent,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { overlayLayers } from '@/ui/overlay-layers';
import { useOverlayPortalContext } from '@/ui/overlay-portal-context';
import { ResolveWidthArgs, useOverlayPosition } from '@/ui/use-overlay-position';
import { CalendarCell, DatePickerPanel } from '@/ui/date-picker-panel';
import {
    buildCalendarDays,
    formatIsoDate,
    monthLabelFormatter,
    normalizeDate,
    pad,
    parseIsoDate,
    toMonthStart,
    WEEK_DAYS,
} from '@/ui/date-picker.utils';

export type DatePickerProps = InputHTMLAttributes<HTMLInputElement> & {
    selectedDate: string;
    setSelectedDate: (value: string) => void;
    uiSize?: 'default' | 'compact';
};

const PANEL_PADDING = 8;
const PANEL_VERTICAL_OFFSET = 6;
const PANEL_MIN_WIDTH = 272;
const PANEL_ESTIMATED_HEIGHT = 320;

const DatePicker: FC<DatePickerProps> = ({
    value,
    disabled,
    className,
    selectedDate,
    setSelectedDate,
    uiSize = 'default',
    placeholder = 'Выберите дату',
    onChange,
    name,
    id,
    'aria-label': triggerAriaLabel,
}) => {
    const { portalTarget: overlayPortalTarget, isInsideModal } = useOverlayPortalContext();
    const generatedId = useId();
    const triggerId = id || `date-picker-${generatedId}`;
    const panelId = `${triggerId}-panel`;
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const [isMounted, setIsMounted] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState<Date>(() =>
        toMonthStart(parseIsoDate(selectedDate) || new Date()),
    );
    const [activeDate, setActiveDate] = useState<Date>(() => normalizeDate(parseIsoDate(selectedDate) || new Date()));
    const resolvePanelWidth = useCallback(({ triggerRect, viewportWidth, viewportPadding }: ResolveWidthArgs) => {
        const maxAllowedWidth = Math.max(220, viewportWidth - viewportPadding * 2);
        return Math.min(maxAllowedWidth, Math.max(triggerRect.width, PANEL_MIN_WIDTH));
    }, []);

    const { position: panelPosition, updatePosition: updatePanelPosition } = useOverlayPosition({
        anchorRef: triggerRef,
        isOpen,
        viewportPadding: PANEL_PADDING,
        verticalOffset: PANEL_VERTICAL_OFFSET,
        estimatedHeight: PANEL_ESTIMATED_HEIGHT,
        initialWidth: PANEL_MIN_WIDTH,
        resolveWidth: resolvePanelWidth,
    });

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (value === undefined || value === null) return;
        setSelectedDate(String(value));
    }, [value, setSelectedDate]);

    useEffect(() => {
        const parsed = parseIsoDate(selectedDate);
        if (!parsed) return;

        setCalendarMonth(toMonthStart(parsed));
        setActiveDate(normalizeDate(parsed));
    }, [selectedDate]);

    const displayDate = useMemo(() => {
        const parsed = parseIsoDate(selectedDate);
        if (!parsed) return placeholder;

        return `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()}`;
    }, [selectedDate, placeholder]);

    const selectedIso = useMemo(() => {
        return parseIsoDate(selectedDate) ? selectedDate : '';
    }, [selectedDate]);

    const todayIso = useMemo(() => formatIsoDate(new Date()), []);
    const activeIso = useMemo(() => formatIsoDate(activeDate), [activeDate]);

    const emitChange = useCallback(
        (nextValue: string) => {
            if (!onChange) return;

            onChange({ target: { value: nextValue } } as ChangeEvent<HTMLInputElement>);
        },
        [onChange],
    );

    const closeCalendar = useCallback(() => {
        setIsOpen(false);
    }, []);

    const openCalendar = useCallback(() => {
        if (disabled) return;

        const baseDate = normalizeDate(parseIsoDate(selectedDate) || new Date());
        setCalendarMonth(toMonthStart(baseDate));
        setActiveDate(baseDate);
        setIsOpen(true);
    }, [disabled, selectedDate]);

    useEffect(() => {
        if (!isOpen) return;

        updatePanelPosition();

        const requestId = window.requestAnimationFrame(() => {
            panelRef.current?.focus();
        });

        const onPointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;
            if (!target) return;

            if (triggerRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;

            closeCalendar();
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);

        return () => {
            window.cancelAnimationFrame(requestId);
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [isOpen, closeCalendar, updatePanelPosition]);

    const calendarDays = useMemo((): CalendarCell[] => buildCalendarDays(calendarMonth), [calendarMonth]);

    const selectDate = useCallback(
        (date: Date) => {
            const normalized = normalizeDate(date);
            const isoDate = formatIsoDate(normalized);

            setSelectedDate(isoDate);
            emitChange(isoDate);
            closeCalendar();
            window.requestAnimationFrame(() => triggerRef.current?.focus());
        },
        [closeCalendar, emitChange, setSelectedDate],
    );

    const clearDate = useCallback(() => {
        setSelectedDate('');
        emitChange('');
        closeCalendar();
        window.requestAnimationFrame(() => triggerRef.current?.focus());
    }, [closeCalendar, emitChange, setSelectedDate]);

    const goToMonth = (offset: number) => {
        setCalendarMonth((prevDate) => new Date(prevDate.getFullYear(), prevDate.getMonth() + offset, 1));
    };

    const shiftActiveDate = (daysOffset: number) => {
        setActiveDate((prevDate) => {
            const shiftedDate = new Date(prevDate.getFullYear(), prevDate.getMonth(), prevDate.getDate() + daysOffset);
            setCalendarMonth(toMonthStart(shiftedDate));
            return normalizeDate(shiftedDate);
        });
    };

    const onTriggerClick = () => {
        if (isOpen) {
            closeCalendar();
            return;
        }

        openCalendar();
    };

    const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;

        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openCalendar();
        }
    };

    const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        switch (event.key) {
            case 'Escape':
                event.preventDefault();
                closeCalendar();
                window.requestAnimationFrame(() => triggerRef.current?.focus());
                break;
            case 'ArrowLeft':
                event.preventDefault();
                shiftActiveDate(-1);
                break;
            case 'ArrowRight':
                event.preventDefault();
                shiftActiveDate(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                shiftActiveDate(-7);
                break;
            case 'ArrowDown':
                event.preventDefault();
                shiftActiveDate(7);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                selectDate(activeDate);
                break;
            default:
                break;
        }
    };

    const triggerClassName = clsx(
        `w-full text-left bg-white border rounded-md shadow-sm
        transition-all duration-200 ease-in-out
        focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
        hover:border-gray-400`,
        uiSize === 'default' && 'px-4 py-1.5 text-sm leading-5 min-h-[40px]',
        uiSize === 'compact' && 'px-2 py-1 text-xs leading-4 min-h-[32px]',
        disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
            : 'text-gray-900 border-slate-200 cursor-pointer',
    );
    const resolvedPortalTarget = overlayPortalTarget ?? (isMounted ? document.body : null);
    const panelLayer = isInsideModal ? overlayLayers.overlayModalPopover : overlayLayers.overlayPopover;

    return (
        <div className={clsx('relative w-full', className)}>
            <button
                id={triggerId}
                ref={triggerRef}
                type="button"
                className={triggerClassName}
                onClick={onTriggerClick}
                onKeyDown={onTriggerKeyDown}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-controls={isOpen ? panelId : undefined}
                aria-label={triggerAriaLabel}
                data-ui="date-picker-trigger"
                data-state={isOpen ? 'open' : 'closed'}
            >
                <span className={clsx('truncate block', selectedDate ? 'text-slate-900' : 'text-slate-400')}>
                    {displayDate}
                </span>
            </button>
            {name && <input type="hidden" name={name} value={selectedDate} />}
            {resolvedPortalTarget &&
                isOpen &&
                createPortal(
                    <DatePickerPanel
                        panelId={panelId}
                        panelRef={panelRef}
                        panelPosition={panelPosition}
                        panelLayer={panelLayer}
                        monthLabel={monthLabelFormatter.format(calendarMonth)}
                        weekDays={WEEK_DAYS}
                        calendarDays={calendarDays}
                        selectedIso={selectedIso}
                        todayIso={todayIso}
                        activeIso={activeIso}
                        onPanelKeyDown={onPanelKeyDown}
                        onPreviousMonth={() => goToMonth(-1)}
                        onNextMonth={() => goToMonth(1)}
                        onSelectDate={selectDate}
                        onHoverDate={(date) => setActiveDate(normalizeDate(date))}
                        onClearDate={clearDate}
                    />,
                    resolvedPortalTarget,
                )}
        </div>
    );
};
export default DatePicker;
